/**
 * The cards a turn drew, under the answer they belong to
 * (`peterhof_orch`/`peterhof_agent` — `sessionStore.showsTurnCards`).
 *
 * On `peterhof_orch` the board column stays the full summary of the trip and
 * this is the answer's own EVIDENCE, read where the answer is read, so most
 * of it is a compact PREVIEW pointing at the board (`inlineTurnCards.ts`'s
 * `COMPACT` map). What it draws is the graph's SNAPSHOT, never the live board
 * node: ids are upserted, so the node an earlier answer was about may have
 * been rebuilt since, and the earlier answer has to keep showing what it said.
 *
 * **`peterhof_agent` has no board at all** (`hasNoBoard`, #132), so none of
 * its cards points at one: `PlanFullInline` renders the entire `PlanTimeline` (in a
 * sheet behind the Figma «Моя поездка» card since 07.10.2026, `TripCard.tsx`),
 * `MapCard` is the sketch alone, and everything else is the catalogue's own
 * component, whole (`inlineTurnCards.ts`). Only the NEWEST snapshot of each id
 * is editable — `latest`/`bubbleId`, put into `LatestSnapshotContext` per card.
 *
 * **The map is a sketch, and that is not a fallback.** There is exactly one
 * live 2GIS instance per page (`map/mapHost.ts`) and it lives on the board, so
 * the route under an answer is `MapSketch` — keyless, no tiles — plus the
 * route's own totals and «Открыть на карте», which asks the layout to bring the
 * board map into view (`boardPanelStore.requestFocus`, answered by `App.tsx`:
 * the panel on desktop, the «Доска» tab on mobile) — on a graph that has a
 * board; a board-less one gets the sketch and the totals only.
 *
 * **A button here must still do something.** Taps that SEND a phrase (a tile,
 * an event) are the visitor's next utterance and work whatever the board looks
 * like; card-level `actions` name their card and are drawn only while it is on
 * the board — on a board-less graph, only on its newest snapshot
 * (`catalog/inlineCard.ts`).
 */

import { useMemo, type ComponentType } from 'react';
import ErrorBoundary from '../app/ErrorBoundary';
import { useBoardPanel } from '../board/boardPanelStore';
import { InlineCardContext, LatestSnapshotContext } from '../catalog/inlineCard';
import { catalogEntry } from '../catalog/registry';
import CardActions from '../catalog/components/CardActions';
import MapSketch from '../catalog/components/MapSketch';
import NoteCard from '../catalog/components/NoteCard';
import type {
  MapCardProps,
  NoteCardProps,
  ObjectStripProps,
  PriceTableProps,
  TripPlanProps,
} from '../catalog/generated/schemas';
import { send } from '../lib/send';
import { stripEmoji as plain } from '../lib/emoji';
import { cn } from '../lib/cn';
import { hasNoBoard } from '../store/sessionStore';
import TripCard from './TripCard';
import { useSendLocked } from './turnLock';
import type { TurnCard } from '../transport/events';
import {
  clip,
  inlineCards,
  planPreview,
  pricePreview,
  routeSummary,
  stripPreview,
  type InlineCard,
} from './inlineTurnCards';

function ToBoard({ id, label }: { id: string; label: string }) {
  return (
    <button type="button" className="chip" onClick={() => useBoardPanel.getState().requestFocus(id)}>
      {label}
    </button>
  );
}

function MapInline({ card, toBoard }: { card: InlineCard; toBoard: boolean }) {
  const props = card.props as MapCardProps;
  return (
    <div className="turn-card turn-card--map">
      <MapSketch props={props} />
      <div className="turn-card__foot">
        <span className="turn-card__meta">{plain(routeSummary(props))}</span>
        {toBoard ? <ToBoard id={card.id} label="Открыть на карте" /> : null}
      </div>
    </div>
  );
}

function PlanInline({ card }: { card: InlineCard }) {
  const props = card.props as TripPlanProps;
  const { stops, more, totals } = planPreview(props);
  return (
    <article className="card card--plan turn-card">
      <header className="card__head">
        <h3 className="card__title">{plain(props.title ?? 'План поездки')}</h3>
        {props.date ? <span className="card__meta">{plain(props.date)}</span> : null}
      </header>
      <div className="card__pad">
        {totals ? <p className="plan__totals">{totals}</p> : null}
        <ol className="turn-card__list">
          {stops.map((stop, index) => (
            <li key={`${stop.title}:${index}`} className="turn-card__row">
              <span className="turn-card__num">{stop.n ?? '•'}</span>
              <span className="turn-card__title">{plain(stop.title)}</span>
              {stop.hours ? <span className="turn-card__meta">{plain(stop.hours)}</span> : null}
            </li>
          ))}
        </ol>
        {more > 0 ? <p className="turn-card__more">и ещё {more}</p> : null}
      </div>
      <CardActions sourceId={card.id} actions={props.actions} />
      <div className="card__actions">
        <ToBoard id={card.id} label="Весь план на доске" />
      </div>
    </article>
  );
}

function StripInline({ card }: { card: InlineCard }) {
  const props = card.props as ObjectStripProps;
  const { tiles, more } = stripPreview(props);
  const sendLocked = useSendLocked();
  return (
    <article className="card card--sources turn-card">
      {props.title ? (
        <header className="card__head">
          <h3 className="card__title">{plain(props.title)}</h3>
        </header>
      ) : null}
      <ul className="rows">
        {tiles.map((tile, index) => {
          const inner = (
            <>
              <span className="rows__title">{plain(tile.label)}</span>
              {tile.sublabel ? <span className="rows__sub">{plain(tile.sublabel)}</span> : null}
            </>
          );
          return (
            <li key={`${tile.id}:${index}`} className="rows__item">
              {tile.url ? (
                <a className="rows__link" href={tile.url} target="_blank" rel="noreferrer noopener">
                  {inner}
                </a>
              ) : (
                <button
                  type="button"
                  className="rows__link"
                  disabled={sendLocked}
                  onClick={() => send(tile.value ?? `Расскажи про «${tile.label}»`)}
                >
                  {inner}
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {more > 0 ? (
        <div className="card__actions">
          <ToBoard id={card.id} label={`Ещё ${more} — на доске`} />
        </div>
      ) : null}
    </article>
  );
}

function PricesInline({ card }: { card: InlineCard }) {
  const props = card.props as PriceTableProps;
  const { rows, more } = pricePreview(props);
  const links = props.buy_url ? [{ label: 'Купить билет', url: props.buy_url }] : [];
  return (
    <article className="card card--prices turn-card">
      <header className="card__head">
        <h3 className="card__title">{plain(props.title ?? 'Билеты')}</h3>
        {props.date_label ? (
          <span className="card__meta">
            {plain(props.date_label)}
            {props.weekday_label ? `, ${plain(props.weekday_label)}` : ''}
          </span>
        ) : null}
      </header>
      <div className="table-wrap">
        <table className="table">
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.title}:${row.price}`}>
                <th scope="row">{plain(row.title)}</th>
                <td>{plain(row.price)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {more > 0 ? (
        <div className="card__actions">
          <ToBoard id={card.id} label={`Все тарифы (${props.rows.length}) — на доске`} />
        </div>
      ) : null}
      <CardActions sourceId={card.id} actions={props.actions} links={links} />
    </article>
  );
}

function NoteInline({ card }: { card: InlineCard }) {
  const props = card.props as NoteCardProps;
  return <NoteCard props={{ ...props, text: clip(props.text) }} />;
}

/**
 * `peterhof_agent`'s own plan — no board to preview towards, so the FULL
 * `PlanTimeline` renders here (in `TripCard`'s sheet), uncut and with no "на доске" button
 * (`inlineTurnCards.inlineCards`'s `noBoard` branch). `change_note`, when the
 * graph sent one, reads as one line above the list — «Изменено: +Марли,
 * −Эрмитаж» — the same wording `plan_edit`'s own journal already uses
 * elsewhere in this repo's convention for a diff.
 */
function PlanFullInline({ card }: { card: InlineCard }) {
  // Since 07.10.2026 the full plan sits in a sheet behind the design's
  // «Моя поездка» card (`TripCard.tsx`) — still whole, still in the chat.
  return <TripCard props={card.props as TripPlanProps} changeNote={card.changeNote} />;
}

function Inline({ card }: { card: InlineCard }) {
  switch (card.variant) {
    case 'map':
      return <MapInline card={card} toBoard />;
    case 'map-sketch':
      return <MapInline card={card} toBoard={false} />;
    case 'plan':
      return <PlanInline card={card} />;
    case 'plan-full':
      return <PlanFullInline card={card} />;
    case 'strip':
      return <StripInline card={card} />;
    case 'prices':
      return <PricesInline card={card} />;
    case 'note':
      return <NoteInline card={card} />;
    default: {
      // Narrow on the board already: the catalogue's own component, unchanged.
      const Component = catalogEntry(card.component)?.Component as ComponentType<{ props: unknown }> | null;
      return Component ? (
        <div className="turn-card">
          <Component props={card.props} />
        </div>
      ) : null;
    }
  }
}

export default function TurnCards({
  cards,
  assistantId,
  latest,
  bubbleId,
}: {
  cards: readonly TurnCard[];
  assistantId: string;
  /**
   * Board-less graph only: card id → the answer holding its NEWEST snapshot in
   * the thread (`inlineTurnCards.latestSnapshots`), read against `bubbleId`,
   * this answer's own. Absent — the board decides.
   */
  latest?: ReadonlyMap<string, string>;
  bubbleId?: string;
}) {
  const items = useMemo(() => inlineCards(cards, assistantId), [cards, assistantId]);
  if (items.length === 0) return null;
  return (
    <InlineCardContext.Provider value>
      {/* The board-less Agent's cards are the Figma «Widgets»: grey, 20 px
          rounded, no shadow (`chat.css`, `.turn-cards--chat`); a graph with a
          board keeps the board's card chrome. */}
      <div className={cn('turn-cards', hasNoBoard(assistantId) && 'turn-cards--chat')} aria-label="Что показал этот ответ">
        {items.map((card) => (
          <ErrorBoundary key={card.id} label={`turn-card:${card.component}`}>
            <LatestSnapshotContext.Provider value={latest ? latest.get(card.id) === bubbleId : null}>
              <Inline card={card} />
            </LatestSnapshotContext.Provider>
          </ErrorBoundary>
        ))}
      </div>
    </InlineCardContext.Provider>
  );
}
